import { useEffect } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { NewsDataProvider } from './context/NewsDataContext';
import { WhatsAppHelpProvider } from './context/WhatsAppHelpContext';
import { TextSizeProvider } from './context/TextSizeContext';
import { ThemeProvider } from './context/ThemeContext';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPage from './pages/RefundPage';
import CancellationPage from './pages/CancellationPage';
import ContactRoutePage from './pages/ContactRoutePage';
import CookieConsent from './components/CookieConsent';
import LanguagePicker from './components/LanguagePicker';
import { HelpGuideProvider } from './context/HelpGuideContext';
import { initAnalytics } from './utils/analytics';
import { initSentry } from './utils/sentry';
import { parseAppPath } from './utils/storyLinks';

import { GOOGLE_CLIENT_ID } from './config/env.js';
import { isNativeApp } from './utils/platform.js';
import { initMobileShell } from './mobile/initMobileShell.js';

const PUBLIC_PAGES = {
  '/terms': TermsPage,
  '/privacy': PrivacyPage,
  '/refund': RefundPage,
  '/cancellation': CancellationPage,
  '/contact': ContactRoutePage,
};

function normalizePath() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  return path;
}

function isBrowsePath(path) {
  if (path === '/' || path === '/login') return true;
  const parsed = parseAppPath(path);
  return parsed.type === 'story' || parsed.type === 'edition';
}

function AppContent() {
  const { user } = useAuth();
  const path = normalizePath();

  useEffect(() => {
    if (!isNativeApp() || !user) return;
    if (path === '/login' || PUBLIC_PAGES[path]) {
      window.history.replaceState({}, '', '/');
    }
  }, [user, path]);

  const PublicPage = PUBLIC_PAGES[path];
  if (PublicPage && !(isNativeApp() && user)) return <PublicPage standalone />;

  if (path === '/admin' && !isNativeApp()) return <AdminPage />;

  if (isNativeApp() && !user) return <LoginPage />;

  if (path === '/login') {
    if (isNativeApp() && user) return <HomePage />;
    return <LoginPage />;
  }

  if (isBrowsePath(path) || user) return <HomePage />;

  return <HomePage />;
}

export default function App() {
  useEffect(() => {
    initMobileShell();
    initAnalytics();
    void initSentry();
    if (!isNativeApp() && 'serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LanguageProvider>
        <TextSizeProvider>
          <ThemeProvider>
          <AuthProvider>
            <NewsDataProvider>
              <WhatsAppHelpProvider>
                <HelpGuideProvider>
                  <AppContent />
                  <LanguagePicker />
                  <CookieConsent />
                </HelpGuideProvider>
              </WhatsAppHelpProvider>
            </NewsDataProvider>
          </AuthProvider>
          </ThemeProvider>
        </TextSizeProvider>
      </LanguageProvider>
    </GoogleOAuthProvider>
  );
}
